import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import "./Conversations.css";
import { DynamicContext } from "../../Context/DynamicContext";
import TimeIconConv from "../../Assets/Images/Time Icon Conv.svg";
import { get } from "../../helper/Api_helper";
import { useNavigate } from "react-router";
import {
  VYLABS_BASE_URL,
  GET_CHAT_CHANNEL_LIST_URL,
} from "../../helper/url_helper";
import { debounce } from "lodash";
import {
  groupByDateCategory,
  formatDate,
} from "../../Common/Common-Function/CommonFunctions";
import { ReactComponent as NextIconConvo } from "../../Assets/Images/next-icon-convo.svg";
import userIconConvo from "../../Assets/Images/lets-icons-user.svg";
import AIIcon from "../../Assets/Images/convo-header-icon.svg";
import AIDarkIcon from "../../Assets/Images/convo-header-darkicon.svg";
import VybogTALLoaderAnimation from "../../Common/Dynamic-Animations/DynamicAnimations";
import AnimationPlayer from "../../Common/Animation-Player/AnimationPlayer";
import NoDataFoundAnimation from "../../Assets/Json/nodata-found-robot-animation.json";
import Button from "../../Common/Button/Button";
import allIcon from "../../Assets/Images/all-convo-icon-light.svg";
import allDarkIcon from "../../Assets/Images/all-convo-icon-dark.svg";
import candidateIcon from "../../Assets/Images/candidate-icon.svg";
import candidateDarkIcon from "../../Assets/Images/candidate-dark-icon.svg";
import JobsIcon from "../../Assets/Images/jobs-icon.svg";
import JobsDarkIcon from "../../Assets/Images/job-dark-icon.svg";
import jobIcon from "../../Assets/Images/job-convo-icon.svg";

function Conversations() {
  const navigate = useNavigate();
  const { isDarkMode } = useContext(DynamicContext);
  const observer = useRef();
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [chatChannelsList, setChatChannelsList] = useState([]);
  const [searchChannel, setSearchChannel] = useState("");
  const [hoveredChannel, setHoveredChannel] = useState(null);
  const [toggeleShow, setToggeleShow] = useState("all");
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const offsetRef = useRef(offset);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  const handleIntersection = useCallback(
    debounce((entries) => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        setIsLoadingMore(true);
        setOffset((prev) => prev + 10);
      }
    }, 200),
    [hasMore, isLoadingMore],
  );

  const lastElement = useCallback(
    (node) => {
      if (isLoading || !hasMore) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        handleIntersection(entries);
      });

      if (node) observer.current.observe(node);
    },
    [handleIntersection, isLoading, hasMore],
  );

  const handleMouseEnter = (channelID) => {
    setHoveredChannel(channelID);
  };

  const handleMouseLeave = () => {
    setHoveredChannel(null);
  };

  const handleChannelClick = (channelID) => {
    navigate(`/conversation/${channelID}`);
  };

  const fetchChannels = async (forceRefresh = false, forcedOffset = null) => {
    if (isLoading) return;
    setIsLoading(true);
    const currentOffset =
      forcedOffset !== null ? forcedOffset : offsetRef.current;
    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${GET_CHAT_CHANNEL_LIST_URL}?offset=${currentOffset}&limit=10`,
      );
      const chat_channels = response?.chat_channels || [];
      setChatChannelsList((prev) => {
        if (forceRefresh || currentOffset === 0) {
          return chat_channels;
        } else {
          const existingIds = new Set(prev.map((channel) => channel.ID));
          const newChannels = chat_channels.filter(
            (channel) => !existingIds.has(channel.ID),
          );
          return [...prev, ...newChannels];
        }
      });
      setHasMore(chat_channels.length === 10);
    } catch (err) {
      console.error("GET_CHAT_CHANNEL_LIST_URL error", err);
      setHasMore(false);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      setInitialFetchDone(true);
    }
  };

  useEffect(() => {
    setChatChannelsList([]);
    setOffset(0);
    setInitialFetchDone(false);
    fetchChannels(true, 0);
  }, []);

  useEffect(() => {
    if (offset > 0) {
      fetchChannels();
    }
  }, [offset]);

  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  const filterChannels = (channels) => {
    let filtered = channels;
    if (toggeleShow === "candidate") {
      filtered = filtered.filter(
        (channel) => channel.ChatChannelType.toLowerCase() === "candidate",
      );
    } else if (toggeleShow === "job") {
      filtered = filtered.filter(
        (channel) => channel.ChatChannelType.toLowerCase() !== "candidate",
      );
    }
    if (searchChannel) {
      filtered = filtered.filter((channel) =>
        (channel.InitialContent || channel.ID)
          .toLowerCase()
          .includes(searchChannel.toLowerCase()),
      );
    }
    return filtered;
  };

  const getTimeDisplay = (channel) => {
    const created = formatDate(channel.CreatedAt);
    const content = channel.InitialContent || channel.ID;
    if (content.toLowerCase().includes("today")) return "30 min ago";
    if (content.toLowerCase().includes("yesterday")) return "Yesterday";
    if (content.toLowerCase().includes("week")) return "Monday";
    if (content.toLowerCase().includes("frontend")) return "30 min ago";
    if (content.toLowerCase().includes("marketing")) return "3 hours ago";
    if (content.toLowerCase().includes("remote")) return "Yesterday";
    if (content.toLowerCase().includes("devops")) return "Sunday";
    if (content.toLowerCase().includes("ux")) return "Monday";
    return created;
  };

  const getSummaryTitle = (content) => {
    return content.toLowerCase() === "candidate"
      ? "Candidate Screening"
      : "Job Screening";
  };

  const renderConversationCards = () => {
    const categoryOrder = [
      "Today",
      "Yesterday",
      "This week",
      "Previous 7 Days",
      "Previous 30 Days",
      "Older",
    ];
    if (!initialFetchDone) {
      return (
        <div className="loader-container">
          <VybogTALLoaderAnimation
          />
        </div>
      );
    }

    if (chatChannelsList.length === 0) {
      return (
        <div className="no-channels-message">
          <h3 style={{ textAlign: "center", marginBottom: "-50px" }}>
            No conversations were found. You don't have any conversation yet!
          </h3>
          <AnimationPlayer
            animationSrc={NoDataFoundAnimation}
            playerHeight="400px"
            playerWidth="300px"
          />
        </div>
      );
    }

    const filteredChannels = filterChannels(chatChannelsList);
    const groupedChannels = groupByDateCategory(filteredChannels);

    const filteredCategories = categoryOrder.filter((category) => {
      const channels = groupedChannels[category] || [];
      return channels.length > 0;
    });

    if (filteredCategories.length === 0 && chatChannelsList.length > 0) {
      return (
        <div className="no-channels-message">
          <h3 style={{ textAlign: "center", marginBottom: "-50px" }}>
            No matching conversations found. Please refine your search query to
            browse your screening conversations.
          </h3>
          <AnimationPlayer
            animationSrc={NoDataFoundAnimation}
            playerHeight="400px"
            playerWidth="300px"
          />
        </div>
      );
    }

    return (
      <>
        {categoryOrder.map((category) => {
          const channels = groupedChannels[category] || [];
          if (channels.length === 0) return null;

          return (
            <div key={category} className="conversation-category">
              <div className="date-category">
                <span>{category}</span>
              </div>
              <div className="conversation-cards">
                {channels.map((channel, index) => {
                  const channelTitle = channel.Title || `Chat ${index + 1}`;
                  const timeDisplay = getTimeDisplay(channel);
                  const summaryTitle = getSummaryTitle(channel.ChatChannelType);
                  const subtext = channelTitle;

                  return (
                    <div
                      key={channel.ID}
                      className="conversation-card-wrapper"
                      ref={index === channels.length - 1 ? lastElement : null}
                    >
                      <div
                        className="conversation-card"
                        onClick={() => handleChannelClick(channel.ID)}
                        onMouseEnter={() => handleMouseEnter(channel.ID)}
                        onMouseLeave={handleMouseLeave}
                        style={
                          channel.ChatChannelType === "candidate"
                            ? {
                                border: "1px solid var(--border__color)",
                              }
                            : {
                                border: "1px solid var(--border__color)",
                              }
                        }
                      >
                        <div
                          className={
                            channel.ChatChannelType === "candidate"
                              ? "conv-card-header"
                              : "conv-job-card-header"
                          }
                        >
                          <div className="conv-card-icon">
                            <img
                              src={
                                channel.ChatChannelType === "candidate"
                                  ? userIconConvo
                                  : jobIcon
                              }
                              alt="Candidate Name"
                              className="candidate-name-icon"
                            />
                          </div>
                          <h3
                            className="conv-card-title"
                            style={
                              channel.ChatChannelType === "candidate"
                                ? { color: "var(--conversations-headertext)" }
                                : {
                                    color:
                                      "var(--job-conversations-headertext)",
                                  }
                            }
                          >
                            {summaryTitle}
                          </h3>
                        </div>
                        <div className="conv-card-content">
                          <div className="conv-card-subtext">{subtext}</div>
                          <div className="conv-card-footer">
                            <div className="conv-card-time">
                              <img
                                src={TimeIconConv}
                                alt="Time"
                                className="time-icon-convo"
                              />
                              {timeDisplay}
                            </div>
                            <div
                              className="next-icon-convo"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleChannelClick(channel.ID);
                              }}
                              style={
                                channel.ChatChannelType === "candidate"
                                  ? {
                                      background: "var(--conversations-header)",
                                    }
                                  : {
                                      background:
                                        "var(--conversations-job-header)",
                                    }
                              }
                            >
                              <NextIconConvo
                                className="next-icon"
                                style={
                                  channel.ChatChannelType === "candidate"
                                    ? {
                                        color:
                                          "var(--conversations-headertext)",
                                      }
                                    : {
                                        color:
                                          "var(--job-conversations-headertext)",
                                      }
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {isLoadingMore && (
          <div className="loader-container">
            <VybogTALLoaderAnimation
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="conversations-page">
      <div className="header-main-container">
        <div className="header-icon-title-container">
          <img
            src={isDarkMode ? AIDarkIcon : AIIcon}
            alt="Conversations"
            className="header-title-icon"
          />
          <h2 style={{ margin: 0 }}>Conversations</h2>
        </div>
      </div>
      <div className="outer-main-container">
        {chatChannelsList.length > 0 && (
          <div className="convo-toogle-container">
            <div className="buttonoptions">
              <Button
                event={() => setToggeleShow("all")}
                className={
                  toggeleShow === "all" ? "buttonactive" : "buttoninactive"
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  padding: "6px 12px",
                  transition: "all 0.3s ease",
                  gap: "2px",
                }}
              >
                <img
                  src={isDarkMode ? allDarkIcon : allIcon}
                  alt="All"
                  style={{
                    width: "20px",
                    height: "20px",
                    marginRight: "5px",
                  }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  All
                </span>
              </Button>
              <Button
                event={() => setToggeleShow("candidate")}
                className={
                  toggeleShow === "candidate"
                    ? "buttonactive"
                    : "buttoninactive"
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  padding: "6px 12px",
                  transition: "all 0.3s ease",
                  gap: "2px",
                }}
              >
                <img
                  src={isDarkMode ? candidateDarkIcon : candidateIcon}
                  alt="Candidate"
                  style={{
                    width: "20px",
                    height: "20px",
                    marginRight: "5px",
                  }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Candidate
                </span>
              </Button>
              <Button
                event={() => setToggeleShow("job")}
                className={
                  toggeleShow === "job" ? "buttonactive" : "buttoninactive"
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  padding: "6px 12px",
                  transition: "all 0.3s ease",
                  gap: "2px",
                }}
              >
                <img
                  src={isDarkMode ? JobsDarkIcon : JobsIcon}
                  alt="Job"
                  style={{
                    width: "20px",
                    height: "20px",
                    marginRight: "5px",
                  }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Job
                </span>
              </Button>
            </div>
          </div>
        )}
        <div className="outer-container">
          <div className="conversations-content">
            {renderConversationCards()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Conversations;
